import type {
    FilterShortDTO,
    MetaShortDTO,
    RestaurantMarkerDTO,
    RestaurantShortDTO
} from 'shared/types/api'
import type { Coordinates, RestaurantVeryShortDTO, ResultsType } from './types'
import type { Action } from './actions'
import endpoints from 'shared/store/endpoints'
import { managePageAPIError } from 'shared/store/utils'

export type State = {
    metaData: ?MetaShortDTO,
    resultsType: ?ResultsType,
    shortRestaurants: ?Array<RestaurantVeryShortDTO>,
    restaurants: ?Array<RestaurantShortDTO>,
    nearbyRestaurant?: RestaurantShortDTO,
    nearestRestaurants?: Array<RestaurantShortDTO>,
    markers: Array<RestaurantMarkerDTO>,
    userPosition: ?Coordinates,
    center: ?Coordinates,
    defaultCenter: Coordinates,
    resultsCenter: ?Coordinates,
    address: ?string,
    geocodedAddress: ?Coordinates,
    shouldFitBounds?: boolean,
    zoom: number,
    visitedRestaurants: ?Array<RestaurantShortDTO>,
    activeRestaurant: ?RestaurantShortDTO,
    loading?: boolean,
    restaurantLoading?: boolean,
    services: Array<FilterShortDTO>,
    selectedServices: Array<string>,
    geocodingError?: boolean,
    isGeolocationEnabled: boolean,
    error?: any
}

const initialState: State = {
    metaData: null,
    resultsType: null,
    shortRestaurants: null,
    restaurants: null,
    markers: [],
    userPosition: null,
    center: {
        lat: 46.1444605,
        lng: 2.4366511
    },
    defaultCenter: {
        lat: 46.1444605,
        lng: 2.4366511
    },
    resultsCenter: null,
    address: null,
    geocodedAddress: null,
    zoom: 5,
    visitedRestaurants: null,
    selectedServices: [],
    services: [],
    activeRestaurant: null,
    isGeolocationEnabled: false
}
export const reducer = (state: State = initialState, action: Action): State => {
    switch (action.type) {
        case 'LOADING':
            return {
                ...state,
                loading: action.status
            }
        case 'RESTAURANT_LOADING':
            return {
                ...state,
                restaurantLoading: action.status
            }
        case 'PAGE_DATA_STORELOCATOR_RESPONSE':
            return {
                ...state,
                metaData: action.data.metaData,
                markers: action.data.markers,
                services: action.data.services,
                shouldFitBounds: true
            }
        case 'PAGE_API_ERROR':
            return managePageAPIError(
                state,
                action,
                endpoints.pages.storeLocator
            )
        case 'MARKERS_RESULTS':
            return {
                ...state,
                markers: action.data.markers,
                shouldFitBounds: true
            }
        case 'SEARCH_ALL_RESULTS':
            return {
                ...state,
                shortRestaurants: action.data
            }
        case 'SEARCH_RESULTS':
            const {
                address,
                geocodedAddress,
                resultsType,
                restaurants
            } = action
            const {
                selectedServices,
                nearbyRestaurant,
                nearestRestaurants
            } = state
            return {
                ...state,
                geocodingError: false,
                shouldFitBounds: true,
                address: address,
                geocodedAddress: geocodedAddress,
                resultsType: resultsType,
                restaurants,
                nearbyRestaurant:
                    address || selectedServices.length > 0
                        ? nearbyRestaurant
                        : restaurants[0],
                nearestRestaurants:
                    (resultsType === 'nearest' && restaurants) ||
                    nearestRestaurants,
                activeRestaurant: null
            }
        case 'CLEAR_SEARCH':
            return {
                ...state,
                geocodingError: false,
                geocodedAddress: null,
                resultsType: null,
                address: null,
                selectedServices: [],
                restaurants: null,
                activeRestaurant: null
            }
        case 'CLEAR_RESTAURANTS_LIST':
            return {
                ...state,
                geocodingError: false,
                restaurants: null
            }
        case 'SELECT_RESTAURANT':
            return {
                ...state,
                resultsType: action.restaurant ? 'search' : null,
                activeRestaurant: action.restaurant
            }
        case 'SELECTED_SERVICES':
            return {
                ...state,
                selectedServices: action.selectedServices
            }
        case 'RESET_SERVICES':
            return {
                ...state,
                selectedServices: []
            }
        case 'SET_USER_POSITION':
            return {
                ...state,
                userPosition: action.userPosition
            }
        case 'CENTER_ON_USER_POSITION':
            if (!state.userPosition) {
                return state
            }
            return {
                ...state,
                center: state.userPosition
            }
        case 'SHOW_ALERT_ENABLE_GEOLOCATION':
            return {
                ...state,
                isGeolocationEnabled: action.isGeolocationEnabled
            }
        case 'SET_VISITED_RESTAURANTS':
            return {
                ...state,
                visitedRestaurants: action.visitedRestaurants
            }
        case 'MAP_CENTER':
            return {
                ...state,
                center: action.center
            }
        case 'MAP_ZOOM':
            return {
                ...state,
                zoom: action.zoom
            }
        case 'MAP_FIT_BOUNDS':
            return {
                ...state,
                shouldFitBounds: action.shouldFitBounds
            }
        case 'GEOCODING_ERROR':
            return {
                ...state,
                geocodingError: action.geocodingError
            }
        case 'STORE_LOCATOR_ERROR':
            return {
                ...state,
                error: action.error
            }
        case 'RESET':
            return initialState
        case 'PROFILE_RESET_ADD_RESTAURANT_SUCCESS':
            return {
                ...state,
                geocodingError: false,
                geocodedAddress: null,
                resultsType: null,
                address: null,
                selectedServices: [],
                restaurants: null,
                activeRestaurant: null
            }
        default:
            return state
    }
}
